/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Content,
  Header,
  Link,
  Page,
  Table,
  type TableColumn,
} from '@backstage/core-components';
import {
  AnalyticsContext,
  discoveryApiRef,
  fetchApiRef,
  storageApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import { parseEntityRef } from '@backstage/catalog-model';
import { Box, Chip, Typography, makeStyles } from '@material-ui/core';
import {
  ScopeRow,
} from '../home/HomePage';
import {
  filterByScopes,
  useOwnedComponents,
  useScope,
} from '../home/homeScope';
import {
  type PullRow,
  isPersonalMode,
  relativeAge,
  reposInScope,
  scopeReviewRows,
} from '../home/ScopedPullRequestsCard';
import {
  PR_MODE_LABELS,
  PrModeChips,
  normalizePrMode,
  type PrMode,
} from '../home/PrModeChips';
import { fetchPrCounts } from '../home/prCounts';
import { useViewAs } from '../viewAs/ViewAsContext';

// The backend accepts up to 20 repos per request; match it here (the Home card
// samples 15 to keep its fan-out small, the full page uses the maximum).
const PAGE_REPO_CAP = 20;

const useStyles = makeStyles(theme => ({
  scopeRowWrap: {
    marginBottom: theme.spacing(2),
  },
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },
  caption: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  draftChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.75),
  },
}));

/**
 * Full explore surface behind the Home "Open pull requests" card (its
 * deepLink lands here). Same two modes, same persisted choice, same backend --
 * but the whole result set in a Backstage Table (search, column sort, paging
 * for free) instead of the card's 7-row teaser.
 *
 * Scope-repos mode reads the scope persisted by the Home picker; the caption
 * names the active scope so the state isn't invisible. (Putting the full
 * picker on this page too is a known follow-up.)
 */
export const PullRequestsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const storage = useApi(storageApiRef).forBucket('home');

  // ?as=<login> carries the Home "View as" lens onto this page (the card's
  // "View all N" footer links here with it). The backend re-checks the
  // m42-dev-productivity gate on every personal-mode request, so a crafted
  // URL gets 403s, not data. Login chars only; anything else is ignored.
  // The app-wide lens (sidebar) is the source of truth; a `?as=` query param
  // is still honored as a deep-link fallback (e.g. older bookmarks).
  const { viewAsLogin } = useViewAs();
  const location = useLocation();
  const asLogin = useMemo(() => {
    if (viewAsLogin) return viewAsLogin;
    const raw = new URLSearchParams(location.search).get('as') ?? '';
    return /^[\w-]+$/.test(raw) ? raw : undefined;
  }, [viewAsLogin, location.search]);

  const [mode, setMode] = useState<PrMode>(
    () => normalizePrMode(storage.snapshot<string>('prCardMode').value),
  );
  const analytics = useAnalytics();
  const pickMode = (next: PrMode) => {
    setMode(next);
    storage.set('prCardMode', next);
    analytics.captureEvent('switch', `pr-mode:${next}`);
  };

  // Under the lens the ownership graph is the VIEWED user's (their "My
  // work"); the admin's persisted team scopes don't apply to someone else's
  // teams, so they're dropped while as= is present.
  const { value } = useOwnedComponents(asLogin ? `user:default/${asLogin}` : null);
  const teamRefs = value?.teamRefs ?? [];
  const teamsByRef = value?.teamsByRef ?? {};
  const currentUser = value?.currentUser;
  const childrenMap = value?.childrenMap ?? {};
  const persisted = useScope(teamRefs);
  const scopes = asLogin ? [] : persisted.scopes;
  const subTeams = asLogin ? false : persisted.subTeams;
  // Mirror HomePage: only offer the sub-team toggle when some team the user
  // belongs to actually has descendants (a toggle that never does anything
  // is dead UI). Recomputed here so the picker hides it consistently.
  const hasSubTeams = useMemo(
    () => teamRefs.some(ref => (childrenMap[ref] ?? []).length > 0),
    [teamRefs, childrenMap],
  );
  const scopedEntities = useMemo(
    () =>
      filterByScopes(value?.entities ?? [], scopes, {
        directRefs: value?.directRefs ?? [],
        childrenMap: value?.childrenMap ?? {},
        subTeams,
      }),
    [value, scopes, subTeams],
  );
  const repos = reposInScope(scopedEntities, PAGE_REPO_CAP);
  const totalRepos = reposInScope(scopedEntities, Number.MAX_SAFE_INTEGER).length;
  const personal = isPersonalMode(mode);
  const fetchKey = personal
    ? `${mode}:${asLogin ?? 'self'}`
    : `open:${repos.join('|')}`;

  const [raw, setRaw] = useState<PullRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  const PERSONAL_ENDPOINTS: Partial<Record<PrMode, string>> = {
    mine: 'involving',
    authored: 'authored',
    review: 'review-requests',
  };

  useEffect(() => {
    if (!personal && repos.length === 0) {
      setRaw([]);
      return undefined;
    }
    let cancelled = false;
    setRaw(null);
    setFailed(false);
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('pull-requests-reader');
        const url = personal
          ? `${base}/${PERSONAL_ENDPOINTS[mode]}${
              asLogin ? `?as=${encodeURIComponent(asLogin)}` : ''
            }`
          : `${base}/open?${repos.map(r => `repo=${encodeURIComponent(r)}`).join('&')}`;
        const res = await fetchApi.fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { pulls: PullRow[] };
        if (cancelled) return;
        setRaw(body.pulls);
      } catch {
        if (cancelled) return;
        setFailed(true);
        setRaw([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, mode]);

  // 'scope-review' filters the /open rows to those with unanswered review
  // requests; every other mode shows the fetch as-is.
  const pulls =
    raw === null ? null : mode === 'scope-review' ? scopeReviewRows(raw) : raw;

  // Count badges in the mode menu: personal counts from /counts (shared
  // client cache with the Home card), scope counts from the /open rows when
  // a scope mode is active (left as dashes otherwise -- this page doesn't
  // double-fetch /open just for badges).
  const [personalCounts, setPersonalCounts] = useState<{
    involves?: number;
    authored?: number;
    reviewRequested?: number;
  }>({});
  useEffect(() => {
    let cancelled = false;
    setPersonalCounts({});
    fetchPrCounts(discoveryApi, fetchApi, asLogin)
      .then(v => { if (!cancelled) setPersonalCounts(v); })
      .catch(() => { /* badges hide; modes stay usable */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asLogin]);
  const counts: Partial<Record<PrMode, number | null>> = {
    mine: personalCounts.involves ?? null,
    authored: personalCounts.authored ?? null,
    review: personalCounts.reviewRequested ?? null,
    scope: !personal && raw !== null ? raw.length : null,
    'scope-review':
      !personal && raw !== null ? scopeReviewRows(raw).length : null,
  };

  const scopeCaption = (() => {
    const lens = asLogin ? `Viewing as ${asLogin} · ` : '';
    if (mode === 'mine')
      return `${lens}Everything on GitHub that involves ${asLogin ?? 'you'}`;
    if (mode === 'authored')
      return `${lens}Open pull requests ${asLogin ? 'they' : 'you'} opened`;
    if (mode === 'review')
      return `${lens}Direct and team review requests waiting on ${asLogin ? 'them' : 'you'}`;
    const name = (ref: string) => {
      try { return parseEntityRef(ref).name; } catch { return ref; }
    };
    const base =
      scopes.length === 0
        ? 'My work'
        : scopes.length === 1
        ? name(scopes[0])
        : `${scopes.length} teams`;
    const sub = subTeams ? ' (incl. sub-teams)' : '';
    const sample = totalRepos > repos.length ? ` · sampling ${repos.length} of ${totalRepos} repos` : '';
    const queue = mode === 'scope-review' ? ' · awaiting review' : '';
    return `${lens}Scope: ${base}${sub}${sample}${queue}`;
  })();

  const columns: TableColumn<PullRow>[] = [
    {
      title: 'Title',
      field: 'title',
      highlight: true,
      render: row => (
        <>
          <Link
            to={row.url}
            onClick={() =>
              analytics.captureEvent('click', 'pr-deep-link', {
                attributes: { repo: row.repo, mode },
              })
            }
          >
            {row.title}
          </Link>
          {row.draft && (
            <Chip label="draft" size="small" variant="outlined" className={classes.draftChip} />
          )}
        </>
      ),
    },
    { title: 'Repository', field: 'repo', width: '18%' },
    { title: 'Author', field: 'author', width: '16%' },
    {
      title: 'Age',
      field: 'createdAt',
      width: '10%',
      defaultSort: 'desc',
      render: row => relativeAge(row.createdAt),
    },
  ];

  return (
    // surface attribute distinguishes events from the shared ScopeRow /
    // PrModeChips controls here vs their use on Home.
    <AnalyticsContext attributes={{ surface: 'pull-requests-page' }}>
    <Page themeId="tool">
      <Header title="Pull requests" pageTitleOverride="Pull requests" />
      <Content>
        {/* Local scope picker mirrors Home's. Hidden under the "View as" lens
            (scope is forced to the viewed user's "My work" there, so the
            picker would write through to the admin's persisted state while
            having no effect on the displayed list) AND when ScopeRow itself
            would self-hide (single-team user, no sub-teams). The second
            guard mirrors ScopeRow's `hideIf` -- otherwise the wrapping Box's
            margin would leave a dead gap above the mode row for users the
            picker isn't useful for. */}
        {!asLogin && (teamRefs.length > 1 || hasSubTeams) && (
          <Box className={classes.scopeRowWrap}>
            <ScopeRow
              teamRefs={teamRefs}
              scopes={scopes}
              setScopes={persisted.setScopes}
              teamsByRef={teamsByRef}
              currentUser={currentUser}
              subTeams={subTeams}
              setSubTeams={persisted.setSubTeams}
              hasSubTeams={hasSubTeams}
            />
          </Box>
        )}
        <Box className={classes.modeRow}>
          <PrModeChips mode={mode} onChange={pickMode} counts={counts} />
          <Typography className={classes.caption} component="span">
            {scopeCaption}
          </Typography>
        </Box>
        <Table<PullRow>
          title={pulls ? `${pulls.length} open pull requests` : 'Open pull requests'}
          columns={columns}
          data={pulls ?? []}
          isLoading={pulls === null}
          options={{
            search: true,
            paging: true,
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            padding: 'dense',
          }}
          emptyContent={
            <Box p={3}>
              <Typography color="textSecondary">
                {failed
                  ? "Couldn't load pull requests right now."
                  : `No open pull requests for ${PR_MODE_LABELS[mode].toLowerCase()}.`}
              </Typography>
            </Box>
          }
        />
      </Content>
    </Page>
    </AnalyticsContext>
  );
};
